// utils/device-id-service.js
// 按需引入 crypto-js 子模块，避免全量打包到主包
import CryptoJS from 'crypto-js/core';
import 'crypto-js/sha256';
import 'crypto-js/md5';
import 'crypto-js/enc-base64';
import 'crypto-js/enc-utf8';
// H5 环境下动态导入 FingerprintJS
let FingerprintJS = null;

class DeviceIdService {
  // 显式声明私有属性并指定类型
  private initialized: boolean;
  private secretKey: string;
  // 配置项：ID 长度 (16 = 64bit, 20 = 80bit, 32 = 128bit)
  // 16位十六进制数 (64 bit) 的碰撞概率在十亿级设备中依然极低，完全够用
  private readonly HASH_LENGTH = 16;

  constructor() {
    this.initialized = false;
    this.secretKey = this.generateSecretKey();
  }

  /**
   * 生成加密密钥（基于环境特征）
   */
  generateSecretKey() {
    // 从环境变量获取或生成固定密钥
    // 实际项目中应该从服务器动态获取密钥
    const baseKey = import.meta.env.VITE_APP_ENCRYPT_KEY || 'device-id-encryption-key-2024';

    // 添加设备特定信息增强密钥安全性
    try {
      const systemInfo = uni.getSystemInfoSync();
      const deviceSpecific = `${systemInfo.platform}-${systemInfo.system}-${systemInfo.model}`;
      return CryptoJS.SHA256(baseKey + deviceSpecific).toString();
    }
    catch (error) {
      return CryptoJS.SHA256(baseKey).toString();
    }
  }

  /**
   * 初始化服务
   */
  async init() {
    if (this.initialized) return;

    // H5 环境预加载 FingerprintJS
    // #ifdef H5
    try {
      const module = await import('@fingerprintjs/fingerprintjs');
      FingerprintJS = module.default;
    }
    catch (error) {
      console.warn('FingerprintJS 加载失败:', error);
    }
    // #endif

    this.initialized = true;
  }

  /**
   * 获取加密的设备唯一 ID（主入口）
   */
  async getEncryptedDeviceId() {
    await this.init();

    // 先尝试从缓存读取
    const cachedId = this.getCachedDeviceId();
    if (cachedId) {
      return cachedId;
    }

    let deviceId = '';

    // app，直接读取原生uuid
    // #ifdef APP-PLUS
    deviceId = await this.getAppDeviceId();
    if (!deviceId) {
      // 如果原生获取失败，降级生成
      deviceId = await this.getMiniProgramDeviceId();
    }
    // App端通常不需要额外加密，或者仅做简单签名
    const finalId = this.encryptDeviceId(deviceId);
    this.cacheDeviceId(finalId);
    return finalId;
    // #endif

    // H5, 通过FingerprintJS生成一个
    // #ifdef H5
    deviceId = await this.getH5DeviceId();
    if (!deviceId) {
      deviceId = await this.getH5FallbackId();
    }
    const h5FinalId = this.encryptDeviceId(deviceId);
    this.cacheDeviceId(h5FinalId);
    return h5FinalId;
    // #endif

    // 其他平台生成一个id
    const rawDeviceId = await this.getMiniProgramDeviceId();
    // 加密设备 ID
    const encryptedId = this.encryptDeviceId(rawDeviceId);
    // 缓存加密后的 ID
    this.cacheDeviceId(encryptedId);

    return encryptedId;
  }

  /**
   * App 端获取设备 ID
   */
  async getAppDeviceId(): Promise<string> {
    return new Promise((resolve) => {
      // #ifdef APP-PLUS
      try {
        if (plus.device) {
          const deviceId = plus.device.uuid;
          // 检查是否获取到有效的设备ID
          if (deviceId && deviceId !== 'unknown' && deviceId !== 'undefined') {
            resolve(deviceId);
          }
        }
      }
      catch (error) {
        console.warn('App原生设备ID获取失败:', error);
      }
      // #endif

      // 降级方案, 暂不使用，直接返回空
      resolve('');
    });
  }

  /**
   * H5 端使用 FingerprintJS 获取设备 ID
   */
  async getH5DeviceId(): Promise<string> {
    // #ifdef H5
    try {
      // 使用 FingerprintJS
      if (FingerprintJS) {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        return result.visitorId;
      }
      return '';
    }
    catch (error) {
      return '';
    }
    // #endif

    // #ifndef H5
    return '';
    // #endif
  }

  /**
   * H5 降级方案
   */
  async getH5FallbackId(): Promise<string> {
    // #ifdef H5
    try {
      const components = [];

      // 收集浏览器指纹信息
      if (navigator.userAgent) components.push(navigator.userAgent);
      if (navigator.language) components.push(navigator.language);
      if (screen.width) components.push(screen.width);
      if (screen.height) components.push(screen.height);
      if (screen.colorDepth) components.push(screen.colorDepth);
      if (navigator.hardwareConcurrency) components.push(navigator.hardwareConcurrency);

      // 时区
      components.push(new Date().getTimezoneOffset());

      // Canvas 指纹（简化版）
      const canvasFingerprint = await this.getCanvasFingerprint();
      if (canvasFingerprint) components.push(canvasFingerprint);

      const fingerprint = components.join('|');
      const hash = this.generateHash(fingerprint);

      return `h5_fallback_${hash}`;
    }
    catch (error) {
      // H5降级方案失败
      return this.generateFallbackId('h5');
    }
    // #endif

    return this.generateFallbackId('h5');
  }

  /**
   * 获取 Canvas 指纹（H5）
   */
  async getCanvasFingerprint() {
    // #ifdef H5
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = 200;
      canvas.height = 50;

      ctx.textBaseline = 'top';
      ctx.font = '14px \'Arial\'';
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('Hello, Fingerprint', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('Hello, Fingerprint', 4, 17);

      const dataUrl = canvas.toDataURL();
      return CryptoJS.MD5(dataUrl).toString();
    }
    catch (error) {
      return null;
    }
    // #endif

    return null;
  }

  /**
   * 小程序端生成设备 ID
   */
  async getMiniProgramDeviceId() {
    try {
      const systemInfo = uni.getSystemInfoSync();

      // 收集尽可能多的设备信息
      const components = [
        systemInfo.platform,
        systemInfo.brand || '',
        systemInfo.model,
        systemInfo.system,
        systemInfo.version || '',
        systemInfo.SDKVersion || '',
        systemInfo.language || '',
        systemInfo.theme || '',
        systemInfo.fontSizeSetting || '',
        systemInfo.pixelRatio || '',
        systemInfo.safeArea ? `${systemInfo.safeArea.width}x${systemInfo.safeArea.height}` : '',
        systemInfo.windowWidth,
        systemInfo.windowHeight,
      ].filter(Boolean).join('|');

      // 生成基于设备信息的哈希
      const deviceHash = this.generateHash(components);

      // 添加随机成分确保唯一性
      const randomComponent = Math.random().toString(36).substr(2, 9);

      return `mp_${deviceHash}_${randomComponent}`;
    }
    catch (error) {
      console.warn('小程序设备ID生成失败:', error);
      return this.generateFallbackId('mp');
    }
  }

  /**
   * 通用降级方案
   */
  generateFallbackId(platform) {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substr(2, 12);
    return `${platform}_fallback_${timestamp}_${randomStr}`;
  }

  /**
   * 生成哈希
   */
  generateHash(content) {
    return CryptoJS.SHA256(content).toString().substr(0, 16);
  }

  /**
   * 加密设备 ID
   */
  encryptDeviceId(deviceId) {
    // APP 端可直接返回或使用原生加密
    // #ifdef APP-PLUS
    return `app_${deviceId}`;
    // #endif

    // H5 端如果需要混淆可在此处理，目前直接返回指纹ID
    // #ifdef H5
    return `h5_${deviceId}`;
    // #endif

    // 小程序端：使用 SHA256 生成固定签名，避免 AES 随机数报错
    try {
      const timestamp = Date.now();
      // 使用 AES 加密
      const encrypted = CryptoJS.SHA256(`${deviceId}${this.secretKey}${timestamp}`).toString();

      // 添加时间戳和验证信息
      const payload = {
        d: deviceId, // data
        s: encrypted, // sign
        t: timestamp,
        p: uni.getSystemInfoSync().platform, // platform
        v: '1.0',
      };
      const jsonStr = JSON.stringify(payload);
      // 必须使用兼容的 base64 方法
      const finalId = this.miniProgramBtoa(jsonStr).substring(0, this.HASH_LENGTH);
      return `mp_${finalId}`;
    }
    catch (error) {
      console.error('设备ID加密失败:', error);
      // 加密失败时返回原始ID（不推荐在生产环境这样做）
      return `mp_${deviceId}`;
    }
  }

  /**
   * 解密设备 ID（主要用于调试）
   */
  decryptDeviceId(encryptedId: string) {
    try {
      // 使用兼容的 atob
      const jsonStr = this.miniProgramAtob(encryptedId);
      const payload = JSON.parse(jsonStr);

      // 验证签名
      const expectedSign = CryptoJS.SHA256(`${payload.d}${this.secretKey}${payload.t}`).toString();
      if (expectedSign !== payload.s) {
        console.warn('签名验证失败');
        return null;
      }

      return payload.d;
    }
    catch (error) {
      console.error('设备ID解密失败:', error);
      return null;
    }
  }

  miniProgramBtoa(str: string) {
    // 终极降级：使用 CryptoJS 进行 Base64 编码 (最稳妥)
    return CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));
  }

  /**
   * 小程序兼容版 atob
   */
  miniProgramAtob(str: string) {
    // 终极降级：使用 CryptoJS 进行 Base64 解码
    return CryptoJS.enc.Base64.parse(str).toString(CryptoJS.enc.Utf8);
  }

  /**
   * 获取缓存的设备 ID
   */
  getCachedDeviceId() {
    try {
      return uni.getStorageSync('encrypted_device_id');
    }
    catch (error) {
      return null;
    }
  }

  /**
   * 缓存设备 ID
   */
  cacheDeviceId(deviceId) {
    try {
      uni.setStorageSync('encrypted_device_id', deviceId);
    }
    catch (error) {
      console.error('设备ID缓存失败:', error);
    }
  }

  /**
   * 清除缓存的设备 ID
   */
  clearCache() {
    try {
      uni.removeStorageSync('encrypted_device_id');
    }
    catch (error) {
      console.error('清除设备ID缓存失败:', error);
    }
  }

  /**
   * 获取设备详细信息（用于调试和分析）
   */
  async getDeviceInfo() {
    const systemInfo = uni.getSystemInfoSync();
    const deviceId = await this.getEncryptedDeviceId();

    const info = {
      deviceId,
      platform: systemInfo.platform,
      environment: this.getEnvironment(),
      model: systemInfo.model,
      brand: systemInfo.brand,
      system: systemInfo.system,
      version: systemInfo.version,
      SDKVersion: systemInfo.SDKVersion,
      language: systemInfo.language,
      timestamp: Date.now(),
      userAgent: '',
      screen: '',
    };

    // 添加环境特定信息
    // #ifdef H5
    info.userAgent = navigator.userAgent;
    info.screen = `${screen.width}x${screen.height}`;
    // #endif

    return info;
  }

  /**
   * 获取当前环境类型
   */
  getEnvironment() {
    const platform = uni.getSystemInfoSync().platform;
    if (platform === 'web') return 'h5';
    if (platform === 'android' || platform === 'ios') return 'app';
    return 'mini-program';
  }

  /**
   * 验证设备 ID 有效性
   */
  validateDeviceId(deviceId) {
    if (!deviceId) return false;

    try {
      const payload = JSON.parse(atob(deviceId));

      // 检查时间戳（如果超过30天则认为是过期的）
      const thirtyDays = 30 * 24 * 60 * 60 * 1000;
      return Date.now() - payload.timestamp <= thirtyDays;
    }
    catch (error) {
      return false;
    }
  }
}

// 创建单例
export default new DeviceIdService();
